import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Profile {
  id: string;
  email: string | null;
  name: string | null;
  avatar_url: string | null;
  subscription_tier: string | null;
  subscription_status: string | null;
  trial_ends_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  // 雲端儲存限制相關
  cloud_storage_total_bytes: number | null;
  cloud_storage_uploaded_this_month: number | null;
  cloud_storage_month_reset_at: string | null;
  // 試用狀態相關（從資料庫讀取）
  trial_started_at: string | null;
  trial_expires_at: string | null;
  grace_period_expires_at: string | null;
}

export function useProfile() {
  const { user } = useAuth();

  const {
    data: profile,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;
      // 新欄位可能尚未在資料庫中，使用預設值
      const rawData = data as Partial<Profile> | null;
      return {
        ...data,
        cloud_storage_total_bytes: rawData?.cloud_storage_total_bytes ?? 0,
        cloud_storage_uploaded_this_month: rawData?.cloud_storage_uploaded_this_month ?? 0,
        cloud_storage_month_reset_at: rawData?.cloud_storage_month_reset_at ?? null,
        // 試用狀態欄位
        trial_started_at: rawData?.trial_started_at ?? null,
        trial_expires_at: rawData?.trial_expires_at ?? null,
        grace_period_expires_at: rawData?.grace_period_expires_at ?? null,
      } as Profile;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });

  const isTrialing = () => {
    if (!profile) return false;
    return profile.subscription_status === "trialing";
  };

  const getTrialDaysLeft = () => {
    if (!profile?.trial_ends_at) return 0;
    const trialEnd = new Date(profile.trial_ends_at);
    const now = new Date();
    const diff = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getSubscriptionTier = () => {
    return profile?.subscription_tier || "free";
  };

  return {
    profile: profile || null,
    loading,
    error: error as Error | null,
    isTrialing,
    getTrialDaysLeft,
    getSubscriptionTier,
    refetch,
  };
}
